import React, { useState, useMemo, useEffect } from 'react';
import { Params as UrlParams } from 'react-router';
import { useParams } from 'react-router-dom';
import { Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useFormState } from 'react-hook-form';
import { formAPI } from 'api/form';
import GridPopup from 'components/common/grid/GridPopup';
import IconHandler from 'components/common/icon/IconHandler';
import { FormPanelButtonProps } from 'components/common/form';
import useBundleTranslation from 'i18n';
import { processSettingsUrl } from 'components/common/form/formTools';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { ActionMenuItem } from 'components/common/action-menu/ActionMenu';
import showdown from 'showdown';
import { openInNewTab } from 'tools/tools';
import pollingService from 'tools/pollingService';
import { InfoStatusPopupWrapper, useInfoStatusPopup } from 'components/common/popup/InfoStatusPopup';
import { useMiNavigation } from 'hooks/useNavigation';

const converter = new showdown.Converter();

function addWarningMessage(msg: string, data: any) {
    if (data?.data?.warn) {
        msg += '<br/>' + (Array.isArray(data?.data?.warn) ? data.data.warn.join('<br/>') : data.data.warn);
    }
    return msg;
}

const FormPanelGenericButton: React.FC<FormPanelButtonProps<any>> = ({ button, formActions, isSubMenuItem }) => {
    const { t } = useBundleTranslation(button.translationNS ?? 'components/common/form/form');
    const navigate = useMiNavigation();
    const { isDirty } = useFormState({ control: formActions.hookFormControl });

    const queryClient = useQueryClient();
    const urlParams: UrlParams = useParams();
    const [isLoading, toggleLoading] = useState(false);
    const [isModalOpen, toggleModalOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [translateMessage, setTranslateMessage] = useState(true);
    const icon = button.props.icon ? <IconHandler icon={button.props.icon} /> : null;
    const saveBeforeAction = button.props.saveBeforeAction ?? false;

    const responseMessagePopupSettings = {
        title: t('info'),
        textOK: t('ok'),
        noCancel: true,
    };

    const errorMessagePopupSettings = {
        title: t('error'),
        noButtons: true,
    };

    const url = processSettingsUrl(button.props.url ?? '', Object.keys(urlParams), urlParams);
    const popupUrl = useMemo(() => {
        return button.props.ignoreCache ? `${url}?v=${Date.now()}` : url;
    }, [url, button.props.ignoreCache, isModalOpen]);

    const infoPopup = useInfoStatusPopup();
    useEffect(() => {
        infoPopup.setIsOpen(message?.length > 0 || errorMessage?.length > 0);
    }, [message, errorMessage]);
    const updateFormField = () => {
        if (button.props.fieldsToUpdateBeforeSave) {
            Object.entries(button.props.fieldsToUpdateBeforeSave).forEach((item) => {
                formActions.hookFormSetValue(item[0], item[1] ?? '');
            });
        }
    };

    const onSuccess = (data: any) => {
        toggleLoading(false);
        if (button.props.reloadGrids) {
            if (button.props.reloadGrids && button.props.reloadGrids.length > 0) {
                button.props.reloadGrids.forEach((gridName: string) => {
                    queryClient.invalidateQueries({ queryKey: [gridName] });
                });
            }
        }
        const responseMessage = data?.message || data?.data?.message;
        const successMessage = addWarningMessage(
            button.props.successText ? t(button.props.successText) : responseMessage,
            data,
        );

        setMessage(successMessage);
        infoPopup.setStatus({
            info: [successMessage],
        });

        afterHandleClick();
    };

    const onError = (response: { message: string }) => {
        toggleLoading(false);
        const errorMessage = button.props.failedText ? t(button.props.failedText) : response.message;
        setErrorMessage(errorMessage);
        infoPopup.setStatus({
            error: [errorMessage],
        });
        afterHandleClick();
    };

    const afterHandleClick = () => {
        if (button.props.clickCounter) {
            let current = Number(formActions.hookFormGetValues(button.props.clickCounter) ?? '0');
            if (isNaN(current)) {
                current = 0;
            }
            formActions.hookFormSetValue(button.props.clickCounter, current + 1);
        }
    };

    const handleClick = async () => {
        if (button.props.checkFormBeforeSave && isDirty) {
            alert(t('save_before_change_alert'));
            return;
        }

        if (button.props?.confirmBefore) {
            if (!confirm(t(button.props.confirmBefore))) {
                return;
            }
        }

        if (button.props.alertMessage) {
            alert(t(button.props.alertMessage));
            return;
        }

        if (saveBeforeAction && isDirty) {
            const saveResponse = await formActions.formSave();
            if (saveResponse?.data?.status != 'OK') {
                return;
            }
        }

        if (url && !button.props.popupConfig) {
            toggleLoading(true);
            setMessage('');
            setErrorMessage('');
            try {
                let response = null;

                if (button.props.isPost === true) {
                    response = await formAPI.loadByPost(url);
                } else {
                    response = await formAPI.load(url);
                }

                const isOk = response.data?.status === 'OK';

                if (response.data?.status === 'QUEUE') {
                    const { run } = pollingService({
                        onSuccess,
                        onError,
                        requestData: {},
                    });

                    run(response.data?.data.ppl_id);
                    return;
                }

                if (isOk && button.props.gridIds) {
                    await queryClient.invalidateQueries(button.props.gridIds);
                }
                const responseMessage = response?.data?.message || response?.data?.data?.message;
                const successMessage = addWarningMessage(
                    button.props.successText ? t(button.props.successText) : responseMessage,
                    response?.data,
                );
                const failedMessage = addWarningMessage(
                    responseMessage
                        ? responseMessage
                        : button.props.failedText
                          ? t(button.props.failedText)
                          : t('error'),
                    response?.data,
                );
                let statusData: any = {};
                if (isOk) {
                    statusData.success = [button.props.successText ? t(button.props.successText) : responseMessage];
                } else {
                    statusData.error = [responseMessage];
                }

                if (response?.data?.data) {
                    if (response.data.data?.warn) {
                        response.data.data.warning = response.data.data.warn;
                    }
                    statusData = {
                        ...statusData,
                        ...response.data.data,
                    };
                }
                infoPopup.setStatus(statusData);

                if (button.props.successText) setTranslateMessage(false);
                else
                    setTranslateMessage(
                        response?.data?.data?.translateMessage ?? response?.data?.translateMessage ?? true,
                    );

                setMessage(isOk ? successMessage : '');

                toggleLoading(false);
                if (isOk && response.data.redirectUrl) {
                    navigate(response.data.redirectUrl);
                }

                if (isOk && button.props.refreshForm) {
                    await formActions.formRefetch(button.props?.resetAfterRefreshForm);
                }

                if (!isOk) {
                    setErrorMessage(failedMessage);
                }
            } catch (error: any) {
                setErrorMessage(
                    button.props.failedText ?? error?.response?.data?.message ?? error?.message ?? t('error'),
                );
                toggleLoading(false);
            }
        }

        if (button.props.popupConfig) {
            toggleModalOpen(true);
        }

        if (button.props.type === 'submit') {
            await updateFormField();
            await formActions.formSave();
        }

        if (button.props.redirectTo) {
            let redirectTo = processSettingsUrl(button.props.redirectTo, Object.keys(urlParams), urlParams);
            const matches = redirectTo.match(/\{([^}]+)\}/g);
            if (matches)
                for (const match of matches) {
                    let value = formActions.hookFormGetValues(match.replace('{', '').replace('}', ''));

                    if (value) redirectTo = redirectTo.replaceAll(match, value);
                    else redirectTo = redirectTo.replaceAll(match, '');
                }

            if (button.props.redirectNewTab) {
                openInNewTab(redirectTo);
            } else {
                navigate(redirectTo);
            }
        }

        if (button.props.reloadGrids) {
            if (button.props.reloadGrids && button.props.reloadGrids.length > 0) {
                button.props.reloadGrids.forEach((gridName: string) => {
                    queryClient.invalidateQueries({ queryKey: [gridName] });
                });
            }
        }
    };

    if (button.props?.visible == 'N') {
        return null;
    }

    if (button.props?.showOnTabs) {
        const hash = window.location.hash.toLowerCase();
        const allowedTabs: string[] = button.props.showOnTabs.map((tab: string) => '#' + tab.toLowerCase());

        if (!allowedTabs.includes(hash)) {
            return null;
        }
    }

    return (
        <>
            {isSubMenuItem ? (
                <ActionMenuItem
                    label={t(button.label)}
                    desc={button.menuItemDesc ? t(button.menuItemDesc) : ''}
                    icon={button.props.icon?.type === 'mi' ? button.props.icon.value : undefined}
                    onClick={handleClick}
                />
            ) : (
                <>
                    {button.props.viewType == 'iconButton' ? (
                        <Tooltip title={button.label ? t(button.label) : ''}>
                            <IconButton
                                sx={{ p: '6px', fontSize: 12 }}
                                onClick={handleClick}
                                disabled={button.props.disabled || isLoading}
                            >
                                {icon}
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            sx={{ mr: button.props?.mr ?? 1 }}
                            onClick={handleClick}
                            variant={button.props.variant}
                            type={button.props.type ?? 'button'}
                            startIcon={isLoading ? null : icon}
                            disabled={button.props.disabled || isLoading}
                        >
                            {isLoading && <CircularProgress sx={{ marginRight: '5px' }} color="inherit" size={12} />}
                            {t(button.label)}
                        </Button>
                    )}
                </>
            )}

            <InfoStatusPopupWrapper infoStatusPopup={infoPopup} />
            {button.props.popupConfig && url && isModalOpen && (
                <GridPopup
                    popupUrl={popupUrl}
                    handleHidePopup={() => toggleModalOpen(false)}
                    handleConfirmPopup={() => {}}
                    popupConfig={button.props.popupConfig}
                    popupType={button.props.popupConfig?.type ?? 'form'}
                    popupMode={button.props.popupConfig?.mode ?? 'new'}
                    redirectAfter={button.props.popupConfig?.redirectAfter ?? false}
                />
            )}
            {isLoading && button.props.isPost && (
                <LoadingPlaceholder>
                    <Typography>{t(button.props.loadingText ?? 'loading')}</Typography>
                </LoadingPlaceholder>
            )}
        </>
    );
};

export default FormPanelGenericButton;
